import * as am4plugins_wordCloud from '@amcharts/amcharts4/plugins/wordCloud';
import * as am4core from '@amcharts/amcharts4/core';

import { ChartModel } from '../chart.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { ChartMetadataModel } from '../chart-metadata.model';
import { WordCloudSeriesModel } from './word-cloud-series.model';
import { WordCloudChartOptionsModel } from './word-cloud-chart-options.model';
import { DataPointModel } from '../data-point.model';
import { TermTypesEnum } from '../../../../Common/Reports/enums/term-types.enum';

export class WordCloudChartModel extends ChartModel {
    private readonly linkClass = 'agpr-wordCloud-viewPosts-link';

    private wordCloudSeriesModel: WordCloudSeriesModel;
    private currentSeries: am4plugins_wordCloud.WordCloudSeries;

    constructor(
        series: WordCloudSeriesModel[],
        options: WordCloudChartOptionsModel = {},
        metadata?: ChartMetadataModel,
    ) {
        super(series, options, metadata);
        this.wordCloudSeriesModel = series[0];
        this.singleSeries = true;
    }

    public override initChart(element: HTMLElement): void {
        super.initChart(element, am4plugins_wordCloud.WordCloud);
        this.amchart.fontFamily = 'Arial';
        this.amchart.padding(20, 15, 15, 15);

        // on the dashboard page, when the chart is only 1 block height (183 px)
        this.amchart.events.on('sizechanged', ev => {
            const chart = ev.target;
            if (chart.pixelHeight && (chart.pixelHeight < 250)) {
                if (this.currentSeries) {
                    this.currentSeries.minFontSize = 10;
                }
            } else {
                if (this.currentSeries) {
                    this.currentSeries.minFontSize = 15;
                }
            }
        });

        this.setSeries();
    }

    public isContentEmpty(): boolean {
        return !(this.rawData?.[0]?.length);
    }
    public updateSeriesStyles(): void {
    }

    private setSeries() {
        if (this.currentSeries) {
            this.amchart.series.removeValue(this.currentSeries);
        }

        const series = new am4plugins_wordCloud.WordCloudSeries();
        series.randomness = 0;
        series.rotationThreshold = 0;
        series.minFontSize = 15;
        series.accuracy = 1;
        series.dataFields.word = 'tag';
        series.dataFields.value = 'count';

        this.setHeatRules(series);
        this.setSeriesLabels(series);
        this.setSeriesTooltips(series);

        // Word cloud has a max of 10 terms
        series.data = [];
        if (this.rawData) {
            series.data = this.rawData[0].slice(0, (this.options as WordCloudChartOptionsModel).numOfItems);
        }

        this.currentSeries = series;

        this.amchart.series.push(series);
    }

    private setHeatRules(series: am4plugins_wordCloud.WordCloudSeries) {
        const color = this.styles?.colorMap.barColor ?? chartColors.agprBlue;

        series.heatRules.push({
            'target': series.labels.template,
            'property': 'fill',
            'min': am4core.color(color).lighten(0.3),
            'max': am4core.color(color).lighten(-0.5),
            'dataField': 'value'
        });
    }

    public updateData(data: DataPointModel[][]): void {
        if (!this.amchart) {
            return;
        }

        super.updateData(data);

        if (this.rawData) {
            this.currentSeries.data = this.rawData[0].slice(0, (this.options as WordCloudChartOptionsModel).numOfItems);
        }
    }

    private setSeriesLabels(series: am4plugins_wordCloud.WordCloudSeries) {
        series.labels.template.events.off('hit');
        series.labels.clear();

        series.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
        series.labels.template.focusable = true;
        series.labels.template.hoverOnFocus = true;
        if (!this.options.responsive) {
            series.labels.template.events.on(
                'hit',
                (event) => {
                    const tagValue = event.target.currentText;
                    this.wordCloudSeriesModel?.eventHandlers?.wordClicked(tagValue);

                    const mentionLinkElement = document.querySelector(
                        `[id="viewMentionLink"][href*='${event.target.dataItem.dataContext['viewMentionUrl']}']`
                    ) as HTMLElement;

                    if (mentionLinkElement) {
                        mentionLinkElement.click();
                        this.wordCloudSeriesModel?.eventHandlers?.viewPostsClicked(tagValue);
                    }
                    return false;
                }
            );
        }

        if ((this.options as WordCloudChartOptionsModel).hideTooltip) {
            series.labels.template.cursorOverStyle = am4core.MouseCursorStyle.default;
            return;
        }

        let tooltipDescription = '';
        if ((this.options as WordCloudChartOptionsModel).tooltipDescription1 && (this.wordCloudSeriesModel.termType !== TermTypesEnum.WORDS)) {
            const currencyCode = this.options.currencyCode ? (' ' + this.options.currencyCode) : '';
            // eslint-disable-next-line max-len
            tooltipDescription = `${(this.options as WordCloudChartOptionsModel).tooltipDescription1}{count}${currencyCode}${(this.options as WordCloudChartOptionsModel).tooltipDescription2}&nbsp;&nbsp;`;
        }

        let viewMentions = `
            <a
                id="viewMentionLink"
                class="${this.linkClass} viewMentionLink"
                href="{viewMentionUrl}"
                style="
                    pointer-events: auto;
                    color: unset;
                    cursor: pointer;
                    text-decoration: underline;
                    margin: auto;
                    font-weight: normal;
                "
            >
                ${(this.options as WordCloudChartOptionsModel).viewMentionString}
            </a>`;

        if (this.options.responsive) {
            viewMentions = '';
        }

        /**
         * The click event is handled separately in {@link setSeriesTooltips},
         * using an event handler passed on the series model
         */
        series.labels.template.tooltipHTML = (this.options as WordCloudChartOptionsModel).disableViewMentionLink ?
            `<div
                style="margin: 10px; font-size: 0.875rem;"
            >
                <div>
                    <b style="font-weight: 600">{tag}</b>
                </div>
            </div>`
            :
            `<div
                style="margin: 10px; font-size: 0.875rem;"
            >
                <div
                    style="border-bottom: 1px solid #ced4da; padding-bottom: 5px"
                >

                    <b style="font-weight: 600">{tag}</b>
                </div>
                <div style="margin-top: 10px; display: flex; align-items: center">
                    <span>${tooltipDescription}</span>
                    ${viewMentions}
                </div>
            </div>`;
        (this.options as WordCloudChartOptionsModel).tooltipHTML = series.labels.template.tooltipHTML;
    }

    private setSeriesTooltips(series: am4plugins_wordCloud.WordCloudSeries) {
        // set tooltip style
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        series.tooltip.background.stroke = am4core.color('#ced4da');
        series.tooltip.background.cornerRadius = 8;
        series.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        series.tooltip.background.filters.clear();
        series.tooltip.label.padding(0, 0, 0, 0);
        series.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        series.tooltip.events.off('hit');
        series.tooltip.interactionsEnabled = true;
        series.tooltip.keepTargetHover = true;
        series.tooltip.events.on(
            'hit',
            (event) => {
                // We only care when the user clicks on the link, not the tooltip itself
                const isLink = (event.event.target as Element).classList.contains(this.linkClass);
                if (!isLink) {
                    return;
                }

                const tagValue = event.target.dataItem.properties['word'];
                this.wordCloudSeriesModel?.eventHandlers?.viewPostsClicked(tagValue);
                event.event.stopImmediatePropagation();
                return false;
            }
        );
        series.tooltip.adapter.add('tooltipHTML', (text, target) => {
            if (target.dataItem.dataContext['label'] !== 'noData') {
                return (this.options as WordCloudChartOptionsModel).tooltipHTML
                    .replace('{viewMentionUrl}',  target.dataItem.dataContext['viewMentionUrl']);
            }
            return '';
        });
    }

    public clone(): WordCloudChartModel {
        return new WordCloudChartModel(
            [this.wordCloudSeriesModel.clone()],
            this.options,
            this.metadata
        );
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public override setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);
        this.amchart.htmlContainer.style.position = 'relative';
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public override unsetExportStyles(): void {
        super.unsetExportStyles();
    }

    /**
     * Overrides the export chart feature.  Implements custom download for csv & json formats
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {
            label: this.options.label,
            count: (this.options as WordCloudChartOptionsModel).termValueLabel
        };

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        return this.amchart.exporting.export(type);
    }
}
